import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useNotifications } from "../context/NotificationContext";
import { useFeatures } from "../context/FeatureContext";
import { Building2, LogOut, Menu, X, Share2, CalendarDays, ShoppingCart, Wallet, RotateCcw, LayoutDashboard, Upload, Image as ImageIcon, Search, MessageCircle, Users, Shield, Package, Inbox, KeyRound, User as UserIcon, Archive, AlertTriangle, Film, BarChart3, UserCog, FileText, Headset, Globe, Clapperboard, ClipboardList, Store } from "lucide-react";
import NotificationBell from "./NotificationBell";

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const { unreadMessages, unreadReservations, unreadTraderMessages } = useNotifications();
  const { features, permissions } = useFeatures();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const merchantNav = [
    { to: "/dashboard", label: "لوحة التحكم", icon: LayoutDashboard, testid: "nav-dashboard" },
    { to: "/upload", label: "رفع صورة جديدة", icon: Upload, testid: "nav-upload" },
    { to: "/my-photos", label: "صور منتجاتي", icon: ImageIcon, testid: "nav-my-photos" },
    { to: "/search", label: "البحث عن منتج", icon: Search, testid: "nav-search" },
    { to: "/reservations", label: "الحجوزات", icon: Inbox, testid: "nav-reservations", badge: "reservations" },
    { to: "/messages", label: "الرسائل", icon: MessageCircle, testid: "nav-messages", badge: "messages" },
    { to: "/messages/external-sourcing", label: "التوريد الخارجي", icon: Globe, testid: "nav-external-sourcing", sub: true, feature: "external_sourcing" },
    { to: "/insights", label: "إحصائياتي", icon: BarChart3, testid: "nav-insights", feature: "insights" },
    { to: "/invoices", label: "الفواتير", icon: FileText, testid: "nav-invoices" },
    { to: "/share-store", label: "مشاركة الموقع", icon: Share2, testid: "nav-share-store", feature: "public_store" },
    { to: "/store-orders", label: "طلبات المتجر العام", icon: ClipboardList, testid: "nav-store-orders", feature: "public_store" },
    { to: "/employees", label: "الموظفين", icon: Users, testid: "nav-employees" },
    { to: "/branches", label: "الفروع", icon: Building2, testid: "nav-branches" },
    { to: "/sales", label: "البيع الفيزيائي", icon: ShoppingCart, testid: "nav-sales", feature: "physical_sales" },
    { to: "/marketing-videos", label: "الفيديوهات التسويقية", icon: Clapperboard, testid: "nav-marketing-videos", feature: "marketing_videos" },
    { to: "/debts", label: "الدين", icon: Wallet, testid: "nav-debts", feature: "debt" },
    { to: "/returns", label: "المرتجع", icon: RotateCcw, testid: "nav-returns", feature: "returns" },
    { to: "/contact-admin", label: "تواصل مع الإدارة", icon: Headset, testid: "nav-contact-admin" },
    { to: "/profile", label: "الملف الشخصي", icon: UserIcon, testid: "nav-profile" },
    { to: "/change-password", label: "تغيير كلمة المرور", icon: KeyRound, testid: "nav-change-password" },
  ];

  const adminNav = [
    { to: "/admin", label: "لوحة الإدارة", icon: Shield, testid: "nav-admin" },
    { to: "/admin/merchants", label: "التجار", icon: Users, testid: "nav-admin-merchants" },
    { to: "/admin/messages", label: "الرسائل", icon: MessageCircle, testid: "nav-admin-messages", badge: "admin-messages" },
    { to: "/admin/whatsapp", label: "واتساب", icon: MessageCircle, testid: "nav-admin-whatsapp" },
    { to: "/admin/deleted", label: "المحذوفون", icon: Archive, testid: "nav-admin-deleted" },
    { to: "/admin/photos", label: "جميع الصور", icon: Package, testid: "nav-admin-photos" },
    { to: "/admin/reservations", label: "الحجوزات والمحادثات", icon: Inbox, testid: "nav-admin-reservations" },
    { to: "/admin/market-alerts", label: "تنبيهات السوق", icon: AlertTriangle, testid: "nav-admin-market-alerts" },
    { to: "/stores", label: "السوق العام", icon: Store, testid: "nav-admin-marketplace" },
    { to: "/admin/name-requests", label: "طلبات تعديل الأسماء", icon: UserCog, testid: "nav-admin-name-requests" },
    { to: "/admin/founder", label: "إحصائيات إمداد", icon: BarChart3, testid: "nav-admin-founder" },
    { to: "/invoices", label: "الفواتير", icon: FileText, testid: "nav-admin-invoices" },
    { to: "/admin/stories", label: "الستوري", icon: Film, testid: "nav-admin-stories" },
    { to: "/change-password", label: "تغيير كلمة المرور", icon: KeyRound, testid: "nav-change-password" },
  ];

  const employeeNav = [
    { to: "/reservations", label: "الحجوزات", icon: Inbox, testid: "nav-reservations", badge: "reservations" },
    { to: "/invoices", label: "الفواتير", icon: FileText, testid: "nav-invoices" },
    { to: "/search", label: "البحث عن منتج", icon: Search, testid: "nav-search" },
    { to: "/upload", label: "رفع منتج", icon: Upload, testid: "nav-employee-upload" },
    { to: "/messages", label: "الرسائل", icon: MessageCircle, testid: "nav-messages", badge: "messages" },
    { to: "/employee/leaves", label: "الإجازات", icon: CalendarDays, testid: "nav-employee-leaves" },
    { to: "/sales", label: "البيع الفيزيائي", icon: ShoppingCart, testid: "nav-sales", perm: "physical_sales" },
    { to: "/store-orders", label: "طلبات المتجر العام", icon: ClipboardList, testid: "nav-store-orders", perm: "physical_sales" },
    { to: "/debts", label: "الدين", icon: Wallet, testid: "nav-debts", perm: "debt" },
    { to: "/returns", label: "المرتجع", icon: RotateCcw, testid: "nav-returns", perm: "returns" },
  ];
  const nav = (user?.role === "admin" ? adminNav : user?.role === "employee" ? employeeNav : merchantNav).filter((i) => (!i.feature || !features || features[i.feature] !== false) && (!i.perm || (permissions || []).includes(i.perm) || (i.perm === "debt" && ((permissions || []).includes("debt_correct") || (permissions || []).includes("customer_refunds")))));

  const handleLogout = () => { logout(); navigate("/login"); };

  const badgeFor = (item) =>
    item.badge === "messages" ? unreadMessages : item.badge === "reservations" ? unreadReservations
      : item.badge === "admin-messages" ? unreadTraderMessages : 0;

  const NavItem = ({ item }) => {
    const Icon = item.icon;
    const active = location.pathname === item.to || (item.to !== "/admin" && item.to !== "/dashboard" && location.pathname.startsWith(item.to));
    const badgeCount = badgeFor(item);
    return (
      <Link
        to={item.to}
        onClick={() => setOpen(false)}
        data-testid={item.testid}
        className={`flex items-center gap-3 px-4 py-3 rounded-lg font-bold transition-colors ${item.sub ? "mr-6 py-2 text-sm" : ""} ${active ? "bg-[#09637E] text-white" : "text-[#333333] hover:bg-[#EBF4F6]"}`}
      >
        <Icon size={item.sub ? 17 : 20} />
        <span className="flex-1">{item.label}</span>
        {badgeCount > 0 && (
          <span
            data-testid={`${item.badge}-unread-badge`}
            className="min-w-[22px] h-[22px] px-1.5 rounded-full bg-red-500 text-white text-[11px] font-extrabold flex items-center justify-center"
          >
            {badgeCount > 9 ? "+9" : badgeCount}
          </span>
        )}
      </Link>
    );
  };

  return (
    <div className="min-h-screen bg-[#EBF4F6]" dir="rtl">
      {/* Top bar (mobile) */}
      <header className="md:hidden sticky top-0 z-50 bg-white border-b border-[#7AB2B2] px-4 h-16 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-[#09637E] flex items-center justify-center text-white font-extrabold">إ</div>
          <span className="font-extrabold text-[#09637E] text-lg">إمداد</span>
        </div>
        <div className="flex items-center gap-1">
          <NotificationBell />
          <button onClick={() => setOpen(!open)} className="p-2 rounded-lg text-[#09637E]" data-testid="mobile-menu-toggle">
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </header>

      {/* Sidebar */}
      <aside className={`${open ? "block" : "hidden"} md:block fixed md:right-0 top-16 md:top-0 bottom-0 md:bottom-auto right-0 w-full md:w-64 md:h-screen bg-white border-l border-[#7AB2B2] z-40 overflow-y-auto`} data-testid="sidebar">
        <div className="hidden md:flex items-center justify-between p-6 border-b border-[#7AB2B2]">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-[#09637E] flex items-center justify-center text-white font-extrabold text-xl">إ</div>
            <div>
              <div className="font-extrabold text-[#09637E] text-xl leading-none">إمداد</div>
              <div className="text-xs text-[#666666] mt-1">تجار السيراميك</div>
            </div>
          </div>
          {user && <NotificationBell />}
        </div>

        <nav className="p-3 space-y-1">
          {nav.map((item) => <NavItem key={item.to} item={item} />)}
        </nav>

        <div className="p-3 mt-4 border-t border-[#7AB2B2]">
          <div className="px-4 py-3 mb-2 bg-[#EBF4F6] rounded-lg">
            <div className="text-xs text-[#666666]">{user?.role === "admin" ? "المدير" : user?.role === "employee" ? "موظف" : "التاجر"}</div>
            <div className="font-bold text-[#333333] truncate" data-testid="user-name">{user?.role === "employee" ? user?.first_name : (user?.store_name || user?.email)}</div>
            {user?.role === "employee" && user?.store_name && <div className="text-xs text-[#666666] mt-1">{user.store_name}</div>}
            {user?.city && <div className="text-xs text-[#666666] mt-1">{user.city}</div>}
          </div>
          <button
            onClick={handleLogout}
            data-testid="logout-btn"
            className="w-full flex items-center justify-center gap-2 h-12 rounded-lg border-2 border-[#09637E] text-[#09637E] font-bold hover:bg-[#EBF4F6] transition-colors"
          >
            <LogOut size={18} />
            تسجيل الخروج
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="md:mr-64 min-h-screen">
        <div className="max-w-7xl mx-auto p-4 md:p-8 animate-fade-in">{children}</div>
      </main>
    </div>
  );
};

export default Layout;
